import React from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserMusic } from '@fortawesome/pro-light-svg-icons/faUserMusic';
import { faMusic } from '@fortawesome/pro-light-svg-icons/faMusic';
import { faListMusic } from '@fortawesome/pro-light-svg-icons/faListMusic';
import { TypedMemo } from '../../../common/types';
import type { BrowseSearchData } from '../hooks/useBrowseSearch';
import { browseMessages as messages } from '../browseMessages';
import { BrowseResultPanel } from './BrowseResultPanel';
import { BrowseArtistRow } from './BrowseArtistRow';
import { BrowseTrackRow } from './BrowseTrackRow';
import { BrowsePlaylistCard } from './BrowsePlaylistCard';
/* eslint-disable css-modules/no-unused-class -- a shared stylesheet; each part uses its own slice of it */
import styles from './BrowseRows.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface SectionProps<Page> {
  page: Page;
  onSeeAll?: () => void;
  onShowMore?: () => void;
}

type ArtistsPage = NonNullable<BrowseSearchData['artists']>;
type TracksPage = NonNullable<BrowseSearchData['tracks']>;
type PlaylistsPage = NonNullable<BrowseSearchData['playlists']>;

const BrowseArtistsSectionComponent: React.FC<SectionProps<ArtistsPage>> = ({ page, onSeeAll, onShowMore }) => {
  const intl = useIntl();
  return (
    <BrowseResultPanel
      title={intl.formatMessage(messages.typeArtists)}
      icon={<FontAwesomeIcon icon={faUserMusic} />}
      total={page.total}
      totalIsCapped={page.totalIsCapped}
      shown={page.items.length}
      onSeeAll={onSeeAll}
      onShowMore={onShowMore}
      columns={
        <div className={clsx(styles.resultHead, styles.artistGrid)} aria-hidden="true">
          <span />
          <span>{intl.formatMessage(messages.colArtist)}</span>
          <span className={styles.right}>{intl.formatMessage(messages.statFollowers)}</span>
          <span className={styles.right}>{intl.formatMessage(messages.statReach)}</span>
          <span className={styles.right}>{intl.formatMessage(messages.stat7d)}</span>
          <span className={styles.right}>{intl.formatMessage(messages.colToOpen)}</span>
          <span />
        </div>
      }
    >
      {page.items.map((artist) => (
        <BrowseArtistRow key={artist.id} artist={artist} />
      ))}
    </BrowseResultPanel>
  );
};

export const BrowseArtistsSection = TypedMemo(BrowseArtistsSectionComponent);

const BrowseTracksSectionComponent: React.FC<SectionProps<TracksPage>> = ({ page, onSeeAll, onShowMore }) => {
  const intl = useIntl();
  return (
    <BrowseResultPanel
      title={intl.formatMessage(messages.typeTracks)}
      icon={<FontAwesomeIcon icon={faMusic} />}
      total={page.total}
      totalIsCapped={page.totalIsCapped}
      shown={page.items.length}
      onSeeAll={onSeeAll}
      onShowMore={onShowMore}
      columns={
        <div className={clsx(styles.resultHead, styles.trackGrid)} aria-hidden="true">
          <span />
          <span />
          <span>{intl.formatMessage(messages.colTrack)}</span>
          <span className={styles.right}>{intl.formatMessage(messages.statPlaylists)}</span>
          <span className={styles.right}>{intl.formatMessage(messages.statReach)}</span>
          <span className={styles.right}>{intl.formatMessage(messages.stat7d)}</span>
          <span className={styles.right}>{intl.formatMessage(messages.colToOpen)}</span>
          <span />
        </div>
      }
    >
      {page.items.map((track, index) => (
        <BrowseTrackRow key={track.id} track={track} rank={index + 1} />
      ))}
    </BrowseResultPanel>
  );
};

export const BrowseTracksSection = TypedMemo(BrowseTracksSectionComponent);

const BrowsePlaylistsSectionComponent: React.FC<SectionProps<PlaylistsPage>> = ({ page, onSeeAll, onShowMore }) => {
  const intl = useIntl();
  return (
    <BrowseResultPanel
      title={intl.formatMessage(messages.typePlaylists)}
      icon={<FontAwesomeIcon icon={faListMusic} />}
      total={page.total}
      totalIsCapped={page.totalIsCapped}
      shown={page.items.length}
      note={intl.formatMessage(messages.playlistsFree)}
      onSeeAll={onSeeAll}
      onShowMore={onShowMore}
    >
      <div className={styles.playlistGrid}>
        {page.items.map((playlist) => (
          <BrowsePlaylistCard key={playlist.id} playlist={playlist} />
        ))}
      </div>
    </BrowseResultPanel>
  );
};

export const BrowsePlaylistsSection = TypedMemo(BrowsePlaylistsSectionComponent);
